import axios from 'axios';

let handler: (() => void) | null = null;
let interceptorsInstalled = false;
let fetchPatched = false;
/** Avoid duplicate logout if both Axios and fetch see the same 401 in one turn. */
let lastNotifyAt = 0;
const notifyDebounceMs = 400;

/** Call from a component inside `BrowserRouter` + `AuthProvider` to wire logout + redirect. */
export function setSessionExpiredHandler(fn: (() => void) | null) {
  handler = fn;
}

function isLoginRequestUrl(url: string): boolean {
  return /Auth\/login/i.test(url);
}

function notifySessionExpired() {
  const now = Date.now();
  if (now - lastNotifyAt < notifyDebounceMs) return;
  lastNotifyAt = now;
  try {
    handler?.();
  } catch {
    /* ignore */
  }
}

/** Axios 401 on authenticated API calls → sign out + login screen (not failed login POST). */
export function installSessionExpiredInterceptors() {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        const url = String(error.config?.url ?? '');
        if (!isLoginRequestUrl(url)) {
          notifySessionExpired();
        }
      }
      return Promise.reject(error);
    }
  );

  if (typeof window !== 'undefined' && !fetchPatched) {
    fetchPatched = true;
    const w = window as Window & { __pctiqOrigFetch?: typeof fetch };
    const orig = w.__pctiqOrigFetch ?? window.fetch.bind(window);
    w.__pctiqOrigFetch = orig;
    window.fetch = async function fetchWithSessionCheck(
      input: RequestInfo | URL,
      init?: RequestInit
    ): Promise<Response> {
      const res = await orig(input, init);
      if (res.status === 401) {
        const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input);
        if (!isLoginRequestUrl(url)) {
          notifySessionExpired();
        }
      }
      return res;
    };
  }
}
